import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AnnouncementsFeed from '../components/AnnouncementsFeed';
import QuickLinks from '../components/QuickLinks';
import NotificationBell from '../components/NotificationBell';

function statusBadge(s) {
  const map = { 'Pending':'badge-pending','Needs Discussion':'badge-discuss','Discussed':'badge-discussed','Assigned':'badge-discuss','In Progress':'badge-repair','Completed':'badge-completed','Closed':'badge-completed' };
  return <span className={`badge ${map[s] || ''}`}>{s}</span>;
}
const condColor = c => ({ Good:'var(--green)', Fair:'var(--amber)', 'Needs Repair':'var(--orange)', Faulty:'var(--red)' }[c] || 'var(--text2)');
const MAX_IMG = 3 * 1024 * 1024;

// ─── Star rating widget ─────────────────────────────────────────────────────
function StarRating({ value = 0, onChange, readOnly = false, size = 30 }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div style={{ display:'inline-flex', gap:4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} role={readOnly ? undefined : 'button'}
          onClick={() => !readOnly && onChange && onChange(n)}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          style={{ fontSize:size, lineHeight:1, cursor: readOnly ? 'default' : 'pointer',
            color: n <= shown ? '#f5a623' : 'var(--border)', userSelect:'none', transition:'color .1s' }}>★</span>
      ))}
    </div>
  );
}

// ─── Feedback block shown on a Closed ticket ────────────────────────────────
function FeedbackBlock({ taskId }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/feedback/${taskId}`).then(r => { if (r.data) { setRating(r.data.rating); setComment(r.data.comment || ''); setSaved(true); } }).catch(()=>{});
  }, [taskId]);
  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try { await api.post(`/feedback/${taskId}`, { rating, comment }); setSaved(true); }
    catch(e) { window.alert(e.response?.data?.error || 'Could not save feedback'); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop:14, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14 }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>{saved ? 'Your feedback' : 'How was the support you received?'}</div>
      <StarRating value={rating} onChange={setRating} readOnly={saved} />
      {!saved && (
        <>
          <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Any comments? (optional)"
            style={{ width:'100%', marginTop:10 }} />
          <button className="btn btn-primary btn-sm" style={{ marginTop:8 }} onClick={submit} disabled={busy || !rating}>{busy ? '…' : 'Submit feedback'}</button>
        </>
      )}
      {saved && comment && <div style={{ fontSize:13, color:'var(--text2)', marginTop:8 }}>“{comment}”</div>}
      {saved && <div style={{ fontSize:12, color:'var(--green)', marginTop:8 }}>Thank you for your feedback! <button className="btn btn-secondary btn-sm" style={{ marginLeft:6 }} onClick={() => setSaved(false)}>Edit</button></div>}
    </div>
  );
}

// ─── Class Teacher card ─────────────────────────────────────────────────────
function ClassTeacherCard({ user, onChange }) {
  const campuses = user.campuses || [];
  const [maps, setMaps] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ campus_id: campuses.length === 1 ? String(campuses[0].id) : '', room_id:'' });
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => { api.get('/me/class-teacher').then(r => setMaps(r.data)).catch(()=>setMaps([])); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (form.campus_id) api.get(`/master/rooms/${form.campus_id}`).then(r => setRooms(r.data.filter(x => x.active))).catch(()=>setRooms([]));
    else setRooms([]);
  }, [form.campus_id]);

  const submit = async () => {
    if (!form.campus_id || !form.room_id) { setErr('Select campus and room'); return; }
    setBusy(true); setErr('');
    try { await api.post('/me/class-teacher', { campus_id:parseInt(form.campus_id), room_id:parseInt(form.room_id) });
      setAdding(false); setForm({ campus_id: campuses.length===1?String(campuses[0].id):'', room_id:'' }); load(); onChange && onChange();
    } catch(e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const remove = async id => { if (!window.confirm('Remove this class-teacher assignment?')) return; await api.delete(`/me/class-teacher/${id}`); load(); onChange && onChange(); };
  const changeRoom = async (m) => {
    const campusRooms = await api.get(`/master/rooms/${m.campus_id}`).then(r => r.data.filter(x=>x.active));
    const names = campusRooms.map(r => r.name).join(', ');
    const val = window.prompt(`Enter the new room name (available: ${names})`, m.room_name);
    if (!val) return;
    const match = campusRooms.find(r => r.name.toLowerCase() === val.trim().toLowerCase());
    if (!match) { alert('Room not found in your campus.'); return; }
    await api.put(`/me/class-teacher/${m.id}`, { room_id: match.id }); load(); onChange && onChange();
  };

  if (maps === null) return null;
  const statusBadgeCT = s => <span className={`badge ${s==='Approved'?'badge-completed':s==='Rejected'?'badge-faulty':'badge-pending'}`}>{s}</span>;

  return (
    <div className="card" style={{ marginBottom:24, borderLeft:'3px solid var(--accent)' }}>
      <div style={{ fontSize:14, fontWeight:600, marginBottom:10 }}>🎓 Class Teacher</div>
      {maps.length === 0 && !adding && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:'var(--text2)' }}>Are you currently a Class Teacher? Register your classroom to see its assets.</span>
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>Yes</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setMaps([{ _dismissed:true }])}>No</button>
          </div>
        </div>
      )}
      {adding && (
        <div>
          <div className="form-row">
            {campuses.length > 1 && (
              <div className="form-group">
                <label>Campus</label>
                <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value, room_id:'' }))}>
                  <option value="">— Select —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Room Number</label>
              <RoomCombo rooms={rooms} value={form.room_id} disabled={!form.campus_id}
                onChange={rid => setForm(f => ({ ...f, room_id: rid }))} />
            </div>
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>{busy?'Submitting…':'Submit for Approval'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}
      {maps.filter(m => !m._dismissed).map(m => (
        <div key={m.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'8px 0', borderTop:'1px solid var(--border)', flexWrap:'wrap' }}>
          <div style={{ fontSize:13 }}>
            <b>{m.room_name}</b> <span style={{ color:'var(--text2)' }}>· {m.campus_name}</span> {statusBadgeCT(m.status)}
            {m.status === 'Pending' && <span style={{ fontSize:11, color:'var(--text2)', marginLeft:6 }}>awaiting IT approval</span>}
          </div>
          <div className="flex-gap" style={{ gap:6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => changeRoom(m)}>Change Room</button>
            <button className="btn btn-danger btn-sm" onClick={() => remove(m.id)}>Remove</button>
          </div>
        </div>
      ))}
      {maps.length > 0 && !adding && !maps.every(m=>m._dismissed) && (
        <button className="btn btn-secondary btn-sm" style={{ marginTop:8 }} onClick={() => setAdding(true)}>+ Add another classroom</button>
      )}
    </div>
  );
}

// ─── Request Remote Support (RustDesk) ──────────────────────────────────────
function RemoteSupportModal({ onClose }) {
  const [step, setStep] = useState('start');   // start → share
  const [issue, setIssue] = useState('');
  const [reqId, setReqId] = useState(null);
  const [rid, setRid] = useState(''); const [pw, setPw] = useState('');
  const [server, setServer] = useState(null);   // configured RustDesk server
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');

  useEffect(() => { api.get('/remote/request/mine').then(r => { if (r.data) { setReqId(r.data.id); if (r.data.rustdesk_id) { setStep('done'); } else setStep('share'); } }).catch(()=>{}); }, []);
  useEffect(() => { api.get('/remote/settings').then(r => setServer(r.data || {})).catch(()=>{}); }, []);

  const raise = async () => {
    setBusy(true); setErr('');
    try { const r = await api.post('/remote/request', { issue }); setReqId(r.data.id); setStep('share'); }
    catch (e) { setErr(e.response?.data?.error || 'Could not send the request'); } finally { setBusy(false); }
  };
  const share = async () => {
    if (!rid.trim()) { setErr('Enter your RustDesk ID'); return; }
    setBusy(true); setErr('');
    try { await api.put(`/remote/request/${reqId}/id`, { rustdesk_id: rid, rustdesk_pw: pw }); setStep('done'); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save'); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header"><span className="modal-title">🖥 Request Remote Support</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        {step === 'start' && (<>
          <p style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
            IT can view and control your screen with your permission using <b>RustDesk</b>. Tell us the issue and we'll notify your campus IT.
          </p>
          <div className="form-group"><label>What do you need help with?</label>
            <textarea rows={3} value={issue} onChange={e => setIssue(e.target.value)} placeholder="Briefly describe the problem…" /></div>
          {err && <div className="err-msg">{err}</div>}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={raise} disabled={busy}>{busy?'Sending…':'Send Request'}</button>
          </div>
        </>)}

        {step === 'share' && (<>
          <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:12, fontSize:13, lineHeight:1.6 }}>
            <b>Next steps</b>
            <ol style={{ margin:'6px 0 0', paddingLeft:20 }}>
              <li>Download &amp; open RustDesk:&nbsp;
                <a className="btn btn-secondary btn-sm" href="https://rustdesk.com/download" target="_blank" rel="noreferrer">⬇ Download Client</a>
                {server?.server_host
                  ? <div style={{ fontSize:11, color:'var(--text2)', marginTop:3 }}>In RustDesk → Settings → Network, set the ID/Relay server to <b className="mono">{server.server_host}</b> (IT can help).</div>
                  : <div style={{ fontSize:11, color:'var(--text2)', marginTop:3 }}>Install and open the RustDesk client.</div>}
              </li>
              <li>Copy the <b>ID</b> shown in the RustDesk window.</li>
              <li>Paste it below so IT can connect. (Optionally set a one-time password in RustDesk and enter it too.)</li>
            </ol>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Your RustDesk ID *</label>
              <input value={rid} onChange={e => setRid(e.target.value)} placeholder="e.g. 123 456 789" /></div>
            <div className="form-group"><label>Password (optional)</label>
              <input value={pw} onChange={e => setPw(e.target.value)} /></div>
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Later</button>
            <button className="btn btn-primary" onClick={share} disabled={busy}>{busy?'Saving…':'Share with IT'}</button>
          </div>
        </>)}

        {step === 'done' && (<>
          <div style={{ textAlign:'center', padding:'20px 10px' }}>
            <div style={{ fontSize:40 }}>✅</div>
            <p style={{ fontSize:14, marginTop:8 }}>Your details are with IT.</p>
            <p style={{ fontSize:13, color:'var(--text2)' }}>Keep RustDesk open — IT will connect shortly and you'll see a prompt to accept.</p>
          </div>
          <div className="modal-footer"><button className="btn btn-primary" onClick={onClose}>Done</button></div>
        </>)}
      </div>
    </div>
  );
}

// ─── Home ───────────────────────────────────────────────────────────────────
function HomeView({ user, assets, onRaise, go, onCTChange }) {
  const [q, setQ] = useState('');
  const [aiOn, setAiOn] = useState(false);
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [queryId, setQueryId] = useState(null);
  const [rated, setRated] = useState(null);
  const [aiErr, setAiErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [remote, setRemote] = useState(false);
  const personal = assets.personal || [];
  const classroom = assets.classroom || [];
  const examples = ['How do I connect to Wi-Fi?', 'Smart Panel is not working.', 'Projector troubleshooting.', 'How do I reset my password?'];

  useEffect(() => { api.get('/ai/config').then(r => setAiOn(!!r.data.enabled)).catch(()=>{}); }, []);

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setAiErr(''); setAnswer(''); setSources([]); setQueryId(null); setRated(null);
    try {
      const resp = await fetch('/api/ai/ask/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ question: q.trim() }),
      });
      if (!resp.ok || !resp.body) {
        let msg = 'The AI assistant is unavailable. Please raise a ticket.';
        try { msg = (await resp.json()).error || msg; } catch (e) {}
        throw new Error(msg);
      }
      // Parse the Server-Sent Events stream: sources / token / done / error
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buf.indexOf('\n\n')) >= 0) {
          const raw = buf.slice(0, sep); buf = buf.slice(sep + 2);
          const ev = /event:\s*(.+)/.exec(raw)?.[1]?.trim();
          const dataLine = /data:\s*([\s\S]*)/.exec(raw)?.[1];
          if (!ev || dataLine === undefined) continue;
          let payload; try { payload = JSON.parse(dataLine); } catch (e) { continue; }
          if (ev === 'sources') setSources(payload || []);
          else if (ev === 'token') setAnswer(a => a + payload);
          else if (ev === 'done') setQueryId(payload.query_id || null);
          else if (ev === 'error') setAiErr(payload.error || 'The AI assistant is unavailable.');
        }
      }
    } catch (e) { setAiErr(e.message || 'The AI assistant is unavailable. Please raise a ticket.'); }
    finally { setBusy(false); }
  };
  const rate = async helpful => {
    if (!queryId || rated !== null) return;
    setRated(helpful);
    try { await api.post('/ai/feedback', { id: queryId, helpful }); } catch (e) {}
  };
  const onKey = e => { if (e.key === 'Enter' && aiOn) ask(); };

  return (
    <div>
      <h1 style={{ fontSize:24, fontWeight:700, marginBottom:6 }}>Welcome to Our IT Support Portal</h1>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:24 }}>Hello {user.full_name} — {aiOn ? 'ask the assistant for help or raise a ticket below.' : 'search for help or raise a ticket below.'}</p>

      {/* AI self-service */}
      <div className="card" style={{ marginBottom:8, padding:18 }}>
        <div style={{ position:'relative' }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
            placeholder={aiOn ? 'Ask the IT assistant, or describe your issue…' : 'Describe your issue, then raise a ticket…'}
            style={{ width:'100%', fontSize:15, padding:'14px 16px' }} />
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
          {examples.map(ex => (
            <button key={ex} onClick={() => setQ(ex)} className="btn btn-secondary btn-sm" style={{ fontSize:11 }}>{ex}</button>
          ))}
        </div>

        {(busy || answer || aiErr) && (
          <div style={{ marginTop:14, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px' }}>
            {aiErr ? <div style={{ fontSize:13, color:'var(--red)' }}>{aiErr}</div>
              : <div style={{ fontSize:14, whiteSpace:'pre-wrap', lineHeight:1.5 }}><span style={{ fontWeight:600 }}>🤖 </span>{answer}
                  {busy && !answer && <span style={{ color:'var(--text2)' }}>Reading the guides…</span>}
                  {busy && <span className="ai-caret" style={{ opacity:0.6 }}>▌</span>}</div>}
            {sources.length > 0 && (
              <div style={{ marginTop:8, fontSize:12, color:'var(--text2)' }}>Based on: {sources.map((s,i) => (
                <span key={s.id}>{i>0?', ':''}<a href={`/api/documents/${s.id}/file`} target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>{s.title}</a></span>
              ))}</div>
            )}
            {!aiErr && queryId && (
              <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text2)' }}>
                {rated === null ? <>Was this helpful?
                  <button className="btn btn-secondary btn-sm" onClick={() => rate(true)} title="Helpful">👍</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => rate(false)} title="Not helpful">👎</button>
                </> : <span style={{ color:'var(--green)' }}>Thanks for the feedback!</span>}
              </div>
            )}
            <div style={{ marginTop:10, fontSize:12, color:'var(--text2)' }}>Didn’t solve it? <button className="btn btn-secondary btn-sm" onClick={() => onRaise(q)}>Raise a Ticket</button></div>
          </div>
        )}

        <div style={{ marginTop:14, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, color:'var(--text2)' }}>
            {aiOn ? '🤖 AI answers common IT questions instantly. For hardware or on-site help, raise a ticket.'
                  : 'Raise a ticket and IT will help you.'}
          </span>
          <div className="flex-gap" style={{ gap:8 }}>
            {aiOn && <button className="btn btn-primary" onClick={ask} disabled={busy || !q.trim()}>{busy ? 'Thinking…' : '🤖 Ask AI'}</button>}
            <button className="btn btn-secondary" onClick={() => setRemote(true)}>🖥 Request Remote Support</button>
            <button className={`btn ${aiOn ? 'btn-secondary' : 'btn-primary'}`} onClick={() => onRaise(q)}>Raise a Ticket{q ? ' with this' : ''}</button>
          </div>
        </div>
      </div>

      {remote && <RemoteSupportModal onClose={() => setRemote(false)} />}

      {/* Announcements */}
      <div style={{ marginTop:24 }}><AnnouncementsFeed limit={5} /></div>

      {/* Class teacher */}
      <div style={{ marginTop:8 }}><ClassTeacherCard user={user} onChange={onCTChange} /></div>

      {/* My Assets summary */}
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', margin:'2px 0 12px' }}>
        <h2 style={{ fontSize:16, fontWeight:600 }}>My Assets</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => go('assets')}>View all</button>
      </div>
      <AssetTable rows={[...personal, ...classroom]} empty="No assets assigned to you yet." limit={6} />
    </div>
  );
}

// What the holder of the asset needs to know: have I acknowledged this, and if
// not, is something waiting for me? A classroom asset has no acknowledgement of
// its own, so it simply shows a dash rather than a worrying "Pending".
function AckCell({ a }) {
  const s = a.ack_status;
  if (!s || s === 'none') return <span style={{ fontSize:12, color:'var(--text3)' }}>—</span>;
  const map = {
    acknowledged:   ['Acknowledged', 'var(--green)'],
    sent:           ['Awaiting your signature', 'var(--amber)'],
    viewed:         ['Awaiting your signature', 'var(--amber)'],
    link_generated: ['Awaiting your signature', 'var(--amber)'],
    pending:        ['Not sent yet', 'var(--text2)'],
    expired:        ['Link expired — ask IT', 'var(--red)'],
  };
  const [label, colour] = map[s] || [s, 'var(--text2)'];
  return (
    <span style={{ fontSize:12, color:colour, fontWeight:600 }}>
      {label}
      {a.ack_code && <span className="mono" style={{ fontSize:11, color:'var(--text2)',
        fontWeight:400, marginLeft:6 }}>{a.ack_code}</span>}
    </span>
  );
}

function AssetTable({ rows, empty, limit }) {
  const shown = limit ? rows.slice(0, limit) : rows;
  if (!rows.length) return <div className="card"><div className="empty-state"><p>{empty}</p></div></div>;
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Asset ID</th><th>Asset Name</th><th>Type</th><th>Status</th><th>Location</th><th>Assigned</th><th>Acknowledgement</th></tr></thead>
          <tbody>
            {shown.map(a => (
              <tr key={a.id}>
                <td><span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{a.asset_tag}</span></td>
                <td><div style={{ fontWeight:500 }}>{a.brand} {a.model}</div></td>
                <td><span style={{ fontSize:12 }}>{a.asset_type || '—'}</span></td>
                <td><span style={{ fontSize:12, color:condColor(a.condition) }}>● {a.condition}</span></td>
                <td><span style={{ fontSize:12, color:'var(--text2)' }}>{a.room_name || '—'}</span></td>
                {/* assigned_on is the date the asset was actually issued; the
                    old column showed updated_at, which moves whenever anyone
                    edits the record and told the holder nothing. */}
                <td><span style={{ fontSize:12, color:'var(--text2)' }}>
                  {a.assigned_on || (a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '—')}
                </span></td>
                <td><AckCell a={a} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {limit && rows.length > limit && <div style={{ padding:'8px 14px', fontSize:12, color:'var(--text2)' }}>…and {rows.length - limit} more</div>}
    </div>
  );
}

// ─── Assets ─────────────────────────────────────────────────────────────────
function AssetsView({ assets }) {
  const [tab, setTab] = useState('personal');
  const rows = tab === 'personal' ? (assets.personal || []) : (assets.classroom || []);
  return (
    <div>
      <h2 style={{ fontSize:18, fontWeight:600, marginBottom:16 }}>My Assets</h2>
      <div style={{ display:'flex', gap:0, marginBottom:16, borderBottom:'1px solid var(--border)' }}>
        {[['personal',`Personal (${(assets.personal||[]).length})`],['classroom',`Classroom (${(assets.classroom||[]).length})`]].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'9px 20px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
              borderBottom: tab===id?'2px solid var(--accent)':'2px solid transparent',
              color: tab===id?'var(--accent)':'var(--text2)', fontWeight: tab===id?600:400, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>
      <AssetTable rows={rows} empty={tab==='personal' ? 'No personal assets assigned to you.' : 'No classroom assets. Register as a class teacher to see classroom assets here.'} />
    </div>
  );
}

// ─── Profile ────────────────────────────────────────────────────────────────
function ProfileView() {
  const [p, setP] = useState(null);
  const [designation, setDesignation] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [accessories, setAccessories] = useState([]);

  useEffect(() => {
    api.get('/me/profile').then(r => { setP(r.data); setDesignation(r.data.designation || ''); }).catch(()=>{});
    api.get('/me/accessories').then(r => setAccessories(r.data)).catch(()=>{});
  }, []);
  if (!p) return <div style={{ color:'var(--text2)' }}>Loading…</div>;

  const save = async () => {
    setBusy(true); setSaved(false);
    try { await api.put('/me/profile', { designation }); setSaved(true); } catch(e) {}
    finally { setBusy(false); }
  };

  const Row = ({ label, value }) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ fontSize:13, color:'var(--text2)' }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:500 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ maxWidth:560 }}>
      <h2 style={{ fontSize:18, fontWeight:600, marginBottom:16 }}>My Profile</h2>
      <div className="card" style={{ marginBottom:16 }}>
        <Row label="Staff Name" value={p.full_name} />
        <Row label="Employee ID" value={p.employee_id} />
        <Row label="Email" value={p.email} />
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontSize:13, color:'var(--text2)' }}>Designation</span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input value={designation} onChange={e => { setDesignation(e.target.value); setSaved(false); }} placeholder="e.g. Teacher, HOD" style={{ width:200, padding:'5px 8px', fontSize:13 }} />
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? '…' : saved ? '✓' : 'Save'}</button>
          </div>
        </div>
        <Row label="Campus" value={(p.campus_names || []).join(', ')} />
        <Row label="Department" value={p.department} />
      </div>

      {/* Accessories issued by IT (managed by IT Admin — view only) */}
      <div className="card">
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Accessories Issued by IT</div>
        {!accessories.length ? (
          <div style={{ fontSize:13, color:'var(--text2)' }}>No accessories recorded. Items issued by IT (headset, webcam, adapter, etc.) will appear here.</div>
        ) : accessories.map(a => (
          <div key={a.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderTop:'1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:500 }}>{a.accessory_name}{a.serial_number ? <span className="mono" style={{ fontSize:11, color:'var(--text2)', marginLeft:8 }}>SN: {a.serial_number}</span> : null}</div>
              {a.notes && <div style={{ fontSize:11, color:'var(--text2)' }}>{a.notes}</div>}
            </div>
            <span style={{ fontSize:12, color:'var(--text2)' }}>{a.assigned_date ? new Date(a.assigned_date).toLocaleDateString() : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── IT Support Resources ─────────────────────────────────────────────────────
function ResourcesView() {
  const [intercom, setIntercom] = useState([]);
  const [escalation, setEscalation] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get('/master/intercom').then(r => setIntercom(r.data)).catch(()=>{});
    api.get('/master/escalation-resolved').then(r => setEscalation(r.data)).catch(()=>{});
    api.get('/master/it-contacts').then(r => setContacts(r.data)).catch(()=>{});
  }, []);

  // group campus-wise
  const groupByCampus = rows => {
    const m = {};
    rows.forEach(r => { const k = r.campus_name || 'General'; (m[k] = m[k] || []).push(r); });
    return m;
  };
  const interGroups = groupByCampus(intercom);
  const contactGroups = groupByCampus(contacts);

  const Section = ({ title, children }) => (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>{title}</div>
      {children}
    </div>
  );

  const Accordion = ({ groups, render, empty }) => {
    const keys = Object.keys(groups);
    if (!keys.length) return <div style={{ fontSize:13, color:'var(--text2)' }}>{empty}</div>;
    return keys.map(k => (
      <div key={k} style={{ borderTop:'1px solid var(--border)' }}>
        <button onClick={() => setOpen(o => ({ ...o, [k]: !o[k] }))}
          style={{ width:'100%', textAlign:'left', background:'transparent', border:'none', cursor:'pointer',
            padding:'10px 0', fontSize:13, fontWeight:500, color:'var(--text)', display:'flex', justifyContent:'space-between' }}>
          <span>📍 {k}</span><span style={{ color:'var(--text2)' }}>{open[k] ? '▾' : '▸'}</span>
        </button>
        {open[k] && <div style={{ paddingBottom:8 }}>{render(groups[k])}</div>}
      </div>
    ));
  };

  return (
    <div style={{ maxWidth:720 }}>
      <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>IT Support Resources</h2>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>Who to contact and how to reach IT across campuses.</p>

      <Section title="☎️ Intercom Directory">
        <Accordion groups={interGroups} empty="No intercom numbers published yet."
          render={rows => (
            <table><tbody>
              {rows.map(r => (
                <tr key={r.id}><td style={{ fontSize:13, padding:'4px 0' }}>{r.label}</td>
                  <td style={{ fontSize:13, textAlign:'right', fontWeight:600, color:'var(--accent)' }}>{r.number || '—'}</td></tr>
              ))}
            </tbody></table>
          )} />
      </Section>

      <Section title="🚦 Escalation Matrix">
        {!escalation.length ? <div style={{ fontSize:13, color:'var(--text2)' }}>Not configured yet.</div> : (
          Object.entries(groupByCampus(escalation)).map(([campus, rows]) => (
            <div key={campus} style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>📍 {campus}</div>
              <div className="table-wrap"><table>
                <thead><tr><th>Level</th><th>Role</th><th>Name</th><th>Phone</th><th>Email</th></tr></thead>
                <tbody>
                  {rows.map(e => (
                    <tr key={campus + '-' + e.level}>
                      <td><span className="badge badge-pending">L{e.level}</span></td>
                      <td style={{ fontSize:13 }}>{e.role_title || '—'}</td>
                      <td style={{ fontSize:13 }}>{e.name || '—'}</td>
                      <td style={{ fontSize:13 }}>{e.phone || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{e.email || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          ))
        )}
      </Section>

      <Section title="👨‍💻 IT Department Contacts">
        <Accordion groups={contactGroups} empty="No IT contacts published yet."
          render={rows => (
            <table><tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize:13, padding:'4px 0' }}><b>{r.name}</b><span style={{ color:'var(--text2)' }}> · {r.designation || '—'}</span></td>
                  <td style={{ fontSize:13, textAlign:'right' }}>{r.phone || ''} {r.email ? <span style={{ color:'var(--text2)', fontSize:12 }}>· {r.email}</span> : ''}</td>
                </tr>
              ))}
            </tbody></table>
          )} />
      </Section>
    </div>
  );
}

// ─── Room autocomplete (type to filter the list) ────────────────────────────
function RoomCombo({ rooms, value, onChange, disabled }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Keep the visible text in sync with the selected room (and reset on campus change)
  useEffect(() => {
    const sel = rooms.find(r => String(r.id) === String(value));
    setText(sel ? sel.name : '');
  }, [value, rooms]);

  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close); return () => document.removeEventListener('mousedown', close);
  }, []);

  const q = text.trim().toLowerCase();
  const matches = q
    ? rooms.filter(r => r.name.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q))
    : rooms;

  const pick = r => { onChange(String(r.id)); setText(r.name); setOpen(false); };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <input
        value={text}
        disabled={disabled}
        placeholder={disabled ? '— Select campus first —' : 'Type room number or name…'}
        onChange={e => { setText(e.target.value); setOpen(true); if (value) onChange(''); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && !disabled && (
        <div style={{ position:'absolute', left:0, right:0, top:'calc(100% + 2px)', maxHeight:220, overflowY:'auto',
          background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 14px rgba(14,27,71,0.12)', zIndex:1300 }}>
          {matches.length === 0
            ? <div style={{ padding:'8px 12px', fontSize:13, color:'var(--text2)' }}>No matching room</div>
            : matches.map(r => (
                <div key={r.id} onMouseDown={() => pick(r)}
                  style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', color:'var(--text)' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  {r.name}{r.code ? <span style={{ color:'var(--text2)' }}> · {r.code}</span> : ''}
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

// ─── Tickets ────────────────────────────────────────────────────────────────
function TicketsView({ tickets, categories, campuses, user, onSubmitted, prefill, clearPrefill, openTicketId, clearOpenTicket }) {
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ campus_id: campuses.length === 1 ? String(campuses[0].id) : '', room_id:'', category_id:'', description:'' });
  const [rooms, setRooms] = useState([]);
  const [attach, setAttach] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailAtt, setDetailAtt] = useState([]);
  const [detailUpdates, setDetailUpdates] = useState([]);
  const [verifyBusy, setVerifyBusy] = useState(false);

  // load rooms for the selected campus
  useEffect(() => {
    if (form.campus_id) api.get(`/master/rooms/${form.campus_id}`).then(r => setRooms(r.data.filter(x => x.active))).catch(()=>setRooms([]));
    else setRooms([]);
  }, [form.campus_id]);

  const openNew = (text='') => {
    setForm({ campus_id: campuses.length === 1 ? String(campuses[0].id) : '', room_id:'', category_id:'', description: text || '' });
    setAttach(null); setErr(''); setModal(true);
  };
  // open from the home AI box
  useEffect(() => { if (prefill !== null && prefill !== undefined) { openNew(prefill); clearPrefill(); } }, [prefill]); // eslint-disable-line

  const onFile = e => {
    const f = e.target.files[0]; if (!f) return;
    if (!/^image\//.test(f.type)) { setErr('Please attach an image'); return; }
    if (f.size > MAX_IMG) { setErr('Image too large (max 3 MB)'); return; }
    const rd = new FileReader(); rd.onload = ev => setAttach({ filename:f.name, mime_type:f.type, data:ev.target.result }); rd.readAsDataURL(f); setErr('');
  };

  const submit = async () => {
    if (!form.campus_id)    { setErr('Select your campus'); return; }
    if (!form.room_id)      { setErr('Select the room number'); return; }
    if (!form.category_id)  { setErr('Select a category'); return; }
    if (!form.description.trim()) { setErr('Describe your issue'); return; }
    setBusy(true); setErr('');
    const catName = categories.find(c => String(c.id) === String(form.category_id))?.name || 'IT Request';
    try {
      await api.post('/tasks', {
        campus_id: parseInt(form.campus_id), room_id: parseInt(form.room_id), category_id: parseInt(form.category_id),
        title: catName,                       // subject removed → title = category
        description: form.description.trim(),
        reporter_type: 'staff', informed_by_name: user.full_name,
        attachment: attach || undefined,
      });
      setModal(false); onSubmitted();
    } catch(e) { setErr(e.response?.data?.error || 'Could not submit'); }
    finally { setBusy(false); }
  };

  const openDetail = async t => {
    setDetail(t); setDetailAtt([]); setDetailUpdates([]);
    try { const r = await api.get(`/tasks/${t.id}/attachments`); setDetailAtt(r.data); } catch(e) {}
    try { const r = await api.get(`/tasks/${t.id}/updates`); setDetailUpdates(r.data); } catch(e) {}
  };
  // Auto-open a ticket when arriving from the feedback email deep link
  useEffect(() => {
    if (openTicketId && tickets.length) {
      const t = tickets.find(x => x.id === openTicketId);
      if (t) openDetail(t);
      clearOpenTicket && clearOpenTicket();
    }
  }, [openTicketId, tickets]); // eslint-disable-line

  const verifyTicket = async t => {
    if (!window.confirm('Confirm the issue is resolved and close this ticket?')) return;
    setVerifyBusy(true);
    // Keep the modal open and flip to Closed so the feedback (star rating) form appears
    try { await api.post(`/tasks/${t.id}/verify`); setDetail(d => d ? { ...d, status:'Closed' } : d); onSubmitted(); }
    catch(e) { window.alert(e.response?.data?.error || 'Failed'); } finally { setVerifyBusy(false); }
  };

  // Staff may delete a ticket only while it is brand-new (Pending + not yet attended by IT)
  const canDelete = t => t && t.status === 'Pending' && !t.updated_by_name && !t.resolution;
  const deleteTicket = async t => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try { await api.delete(`/tasks/${t.id}`); setDetail(null); onSubmitted(); }
    catch (e) { alert(e.response?.data?.error || 'Could not delete'); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>My Tickets</h2>
        <button className="btn btn-primary" onClick={() => openNew()}>+ Raise a Ticket</button>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!tickets.length ? <div className="empty-state"><h3>No tickets yet</h3><p>Click "Raise a Ticket" to report an issue.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Ticket ID</th><th>Category</th><th>Status</th><th>Created</th><th>Last Updated</th><th>Assigned To</th><th></th></tr></thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.id} style={{ cursor:'pointer' }} onClick={() => openDetail(t)}>
                    <td><span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{t.task_id}</span></td>
                    <td><span style={{ fontSize:12 }}>{t.category_name || t.title}</span></td>
                    <td>{statusBadge(t.status)}</td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{new Date(t.created_at).toLocaleDateString()}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{t.updated_by_name || '—'}</span></td>
                    <td><button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); openDetail(t); }}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Raise ticket — one field (Description) + Category */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:540 }}>
            <div className="modal-header"><span className="modal-title">Raise a Ticket</span><button className="modal-close" onClick={() => setModal(false)}>×</button></div>
            <div className="form-row">
              {campuses.length > 1 && (
                <div className="form-group">
                  <label>Campus *</label>
                  <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id: e.target.value, room_id:'' }))}>
                    <option value="">— Select —</option>
                    {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Room Number *</label>
                <RoomCombo rooms={rooms} value={form.room_id} disabled={!form.campus_id}
                  onChange={rid => setForm(f => ({ ...f, room_id: rid }))} />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description *</label>
              <textarea rows={5} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe your issue in detail…  e.g. Smart panel is not turning on in Grade 6A classroom." />
            </div>
            <div className="form-group">
              <label>Attach a photo <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
              {attach ? (
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <img src={attach.data} alt="" style={{ width:54, height:54, objectFit:'cover', borderRadius:8, border:'1px solid var(--border)' }} />
                  <button className="btn btn-secondary btn-sm" onClick={() => setAttach(null)}>Remove</button>
                </div>
              ) : <input type="file" accept="image/*" onChange={onFile} style={{ padding:'8px 0', background:'transparent', border:'none', color:'var(--text)' }} />}
            </div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Submit Ticket'}</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setDetail(null)}>
          <div className="modal" style={{ maxWidth:540 }}>
            <div className="modal-header"><span className="modal-title"><span className="mono" style={{ color:'var(--accent)' }}>{detail.task_id}</span></span><button className="modal-close" onClick={() => setDetail(null)}>×</button></div>
            <div style={{ marginBottom:12 }}>{statusBadge(detail.status)}</div>
            <div style={{ fontWeight:600, marginBottom:4 }}>{detail.category_name || detail.title}</div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>{detail.description || '—'}</div>
            {detailAtt.map(a => <img key={a.id} src={a.data} alt="" style={{ maxWidth:'100%', borderRadius:8, border:'1px solid var(--border)', marginBottom:10 }} />)}
            {detail.status === 'Completed' && detail.resolution && (
              <div style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.2)', borderRadius:'var(--radius)', padding:12 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--green)', marginBottom:4 }}>✅ Resolution</div>
                <div style={{ fontSize:13 }}>{detail.resolution}</div>
              </div>
            )}

            {/* Progress updates from IT */}
            {detailUpdates.length > 0 && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Progress</div>
                <div style={{ borderLeft:'2px solid var(--border)', paddingLeft:12 }}>
                  {detailUpdates.map(u => (
                    <div key={u.id} style={{ marginBottom:10 }}>
                      <div style={{ fontSize:12 }}>{statusBadge(u.status_at)}</div>
                      {u.note && <div style={{ fontSize:13, marginTop:2 }}>{u.note}</div>}
                      <div style={{ fontSize:11, color:'var(--text3)' }}>{u.by_name || 'IT'} · {new Date(u.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.status === 'Completed' && (
              <div style={{ marginTop:14, background:'rgba(52,211,153,0.06)', border:'1px solid rgba(52,211,153,0.2)', borderRadius:'var(--radius)', padding:12 }}>
                <div style={{ fontSize:13, marginBottom:8 }}>IT has marked this resolved. Please confirm to close the ticket.</div>
                <button className="btn btn-primary" onClick={() => verifyTicket(detail)} disabled={verifyBusy}
                  style={{ background:'var(--green)', borderColor:'var(--green)' }}>{verifyBusy?'…':'✔ Confirm Resolved & Close'}</button>
              </div>
            )}

            {detail.status === 'Closed' && <FeedbackBlock taskId={detail.id} />}

            <div className="modal-footer">
              {canDelete(detail) && <button className="btn btn-danger" onClick={() => deleteTicket(detail)} style={{ marginRight:'auto' }}>Delete Ticket</button>}
              <button className="btn btn-secondary" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Documents (staff read policies & guides) ───────────────────────────────
function DocumentsView() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/documents').then(r => setDocs(r.data)).catch(()=>{}).finally(()=>setLoading(false)); }, []);
  const CAT = { policy:'Policies', guide:'User Guides', other:'Other' };
  const groups = ['policy','guide','other'].map(c => [c, docs.filter(d => d.category===c)]).filter(([,l]) => l.length);
  return (
    <div>
      <h1 style={{ fontSize:22, fontWeight:700, marginBottom:6 }}>Documents</h1>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>Policies and user guides from IT. You can also just ask the assistant on the Home page.</p>
      {loading ? <div style={{ color:'var(--text2)' }}>Loading…</div>
        : !docs.length ? <div className="card"><div className="empty-state"><p>No documents published yet.</p></div></div>
        : groups.map(([cat, list]) => (
          <div key={cat} style={{ marginBottom:22 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>{CAT[cat]}</div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div className="table-wrap"><table>
                <tbody>{list.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight:500 }}>📄 {d.title}<div style={{ fontSize:11, color:'var(--text2)' }}>{d.campus_name || 'All campuses'}</div></td>
                    <td style={{ textAlign:'right' }}><a className="btn btn-secondary btn-sm" href={`/api/documents/${d.id}/file`} target="_blank" rel="noopener noreferrer">Open</a></td>
                  </tr>
                ))}</tbody>
              </table></div>
            </div>
          </div>
        ))}
    </div>
  );
}

// ─── Portal shell ───────────────────────────────────────────────────────────
// Used by Teaching Staff (default) and — with an extra dashboard nav item — by
// Coordinator / Principal / Management, so every role also gets the self-service
// features (raise ticket, my tickets, assets, IT resources, profile).
//   extraNav:    [[id,label], …]  shown above the standard staff nav
//   renderExtra: (pageId) => node for those ids
//   defaultPage: which nav id to open first
//   portalTitle: header label
export default function RequesterPortal({ extraNav = [], renderExtra, defaultPage, portalTitle = 'IT Support Portal' }) {
  const { user, logout } = useAuth();
  const campuses = user.campuses || [];
  const [page, setPage] = useState(defaultPage || 'home');
  // Acknowledgement the notification bell asked us to open, handed to the
  // My Policies page rendered via renderExtra.
  const [policyAck, setPolicyAck] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState({ personal:[], classroom:[] });
  const [prefill, setPrefill] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);

  const loadTickets = useCallback(() => { api.get('/tasks').then(r => setTickets(r.data)).catch(()=>{}); }, []);
  const loadAssets  = useCallback(() => { api.get('/me/assets').then(r => setAssets(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    loadTickets(); loadAssets();
    api.get('/master/categories', { params:{ type:'staff' } }).then(r => setCategories(r.data)).catch(()=>{});
  }, [loadTickets, loadAssets]);

  // Deep link from the feedback email: /?feedback=<taskId> → open that ticket to rate
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('feedback');
    if (id) { setOpenTicketId(parseInt(id)); setPage('tickets');
      window.history.replaceState({}, '', window.location.pathname); }
  }, []);

  const raiseFromHome = text => { setPrefill(text || ''); setPage('tickets'); };

  const nav = [...extraNav, ['home','🏠 Home'],['assets','🖥 Assets'],['tickets','🎫 Tickets'],['resources','📞 IT Resources'],['profile','👤 Profile']];
  const extraIds = extraNav.map(n => n[0]);

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'12px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg2)', boxShadow:'0 1px 3px rgba(14,27,71,0.05)' }}>
        {/* Left: IT logo + School logo + title */}
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <img src={`${process.env.PUBLIC_URL || ''}/it-logo.png`} alt="IT" style={{ height:36, width:'auto', objectFit:'contain' }} />
          <img src={`${process.env.PUBLIC_URL || ''}/school-logo.png`} alt="TIPS Globeducate" style={{ height:44, width:'auto', objectFit:'contain' }} />
          <div style={{ fontWeight:700, fontSize:15, color:'var(--accent)' }}>{portalTitle}</div>
        </div>
        {/* Right: name + Sign out, HRMS/ERP links below */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <NotificationBell onOpen={n => {
              // Staff portals only have My Policies and Tickets to land on.
              if (n.link_page === 'mypolicies') { setPolicyAck(n.link_arg); setPage('mypolicies'); }
              else setPage('tickets');
            }} />
            <span style={{ fontSize:13, color:'var(--text2)' }}>{user.full_name}</span>
            <button className="btn btn-secondary btn-sm" onClick={logout}>Sign out</button>
          </div>
          <QuickLinks />
        </div>
      </div>

      <div style={{ display:'flex', flex:1 }}>
        {/* Left nav */}
        <aside className="sidebar" style={{ width:210, borderRight:'1px solid var(--nav-active)', padding:'14px 8px', flexShrink:0 }}>
          {nav.map(([id,label]) => (
            <button key={id} onClick={() => setPage(id)} className={`sidebar-link${page===id ? ' active' : ''}`}>
              {label}
            </button>
          ))}
        </aside>

        {/* Main */}
        <main style={{ flex:1, overflowY:'auto', padding:24, maxWidth: extraIds.includes(page) ? 1200 : 1000 }}>
          {extraIds.includes(page) && renderExtra && renderExtra(page, setPage, { policyAck, clearPolicyAck: () => setPolicyAck(null) })}
          {page === 'home'    && <HomeView user={user} assets={assets} onRaise={raiseFromHome} go={setPage} onCTChange={loadAssets} />}
          {page === 'assets'  && <AssetsView assets={assets} />}
          {page === 'tickets' && <TicketsView tickets={tickets} categories={categories} campuses={campuses} user={user}
                                    onSubmitted={loadTickets} prefill={prefill} clearPrefill={() => setPrefill(null)}
                                    openTicketId={openTicketId} clearOpenTicket={() => setOpenTicketId(null)} />}
          {page === 'documents' && <DocumentsView />}
          {page === 'resources' && <ResourcesView />}
          {page === 'profile' && <ProfileView />}
        </main>
      </div>
    </div>
  );
}
